import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * A debounced search value: `onSearch` *schedules* the update instead of
 * committing it on every keystroke, so fast typing never triggers a state
 * change — and therefore never re-renders the caller — until the user pauses.
 * Fixes input lag when the search state lives in a component that also renders
 * a heavy table (each keystroke would otherwise re-render the whole panel).
 *
 * Returns `[value, onSearch]`: bind `onSearch` to an AntD `Select`'s `onSearch`
 * (the typed text is shown by AntD internally, so it stays instant) and read
 * `value` to drive the filtered options.
 */
export const useDebouncedSearch = (
  delay = 300
): [string, (text: string) => void] => {
  const [value, setValue] = useState('')
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const onSearch = useCallback(
    (text: string) => {
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setValue(text), delay)
    },
    [delay]
  )

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    []
  )

  return [value, onSearch]
}
